import React from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import Markdown from 'react-native-markdown-display';
import { BlockingModal } from '@/components/ui/blocking-modal';
import { Text } from '@/components/ui/text';
import { usePolicyAcceptance } from '../hooks/use-policy-acceptance';
import { formatPolicyTitle } from '../utils/format-policy-title';
import { policyMarkdownStyles } from '../styles/policy-markdown-styles';
import { sanitizePolicyMarkdown } from '../utils/sanitize-policy-markdown';

export function PolicyAcceptanceGate() {
  const pa = usePolicyAcceptance();

  if (pa.status !== 'blocked') return null;
  if (!pa.currentPolicy) return null;

  const isButtonDisabled = !pa.hasCheckedBox || pa.isAccepting;

  return (
    <BlockingModal
      visible={true}
      title={formatPolicyTitle(pa.currentPolicy.policy_type)}
      primaryAction={{
        label: 'Accept and continue',
        onPress: pa.acceptCurrent,
        disabled: isButtonDisabled,
      }}
      testID="policy-acceptance-modal"
    >
      {pa.currentPolicy.change_summary && (
        <Text className="text-muted-foreground text-sm mb-3">
          {'What changed: '}{pa.currentPolicy.change_summary}
        </Text>
      )}

      <ScrollView className="max-h-80 mb-4 border border-border rounded-lg p-3">
        {pa.isContentLoading ? (
          <ActivityIndicator testID="policy-content-loading" />
        ) : (
          <Markdown style={policyMarkdownStyles}>
            {sanitizePolicyMarkdown(pa.policyContent ?? '')}
          </Markdown>
        )}
      </ScrollView>

      <Pressable
        onPress={() => pa.setHasCheckedBox(!pa.hasCheckedBox)}
        className="flex-row items-center mb-4"
        accessibilityRole="checkbox"
        accessibilityState={{ checked: pa.hasCheckedBox }}
        testID="policy-acceptance-checkbox"
      >
        <View
          className={`w-5 h-5 border-2 rounded mr-3 items-center justify-center ${
            pa.hasCheckedBox ? 'bg-primary border-primary' : 'border-muted-foreground'
          }`}
        >
          {pa.hasCheckedBox && (
            <Ionicons name="checkmark" size={14} className="text-primary-foreground" testID="policy-checkbox-icon" />
          )}
        </View>
        <Text className="text-card-foreground text-sm flex-1">
          {'I have read and accept the '}
          {formatPolicyTitle(pa.currentPolicy.policy_type)}
        </Text>
      </Pressable>

      {pa.unacceptedPolicies.length > 1 && (
        <Text
          className="text-muted-foreground text-xs text-center mb-3"
          testID="policy-sequential-indicator"
        >
          {`Policy ${pa.currentPolicyIndex + 1} of ${pa.unacceptedPolicies.length}`}
        </Text>
      )}

      <Pressable
        onPress={pa.logout}
        className="items-center mt-2"
        testID="policy-logout-link"
      >
        <Text className="text-muted-foreground text-sm underline">Log out</Text>
      </Pressable>
    </BlockingModal>
  );
}
